import React from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  MagnifyingGlass,
  ListChecks,
  Gear,
  SignOut,
  User,
  Translate,
  UsersThree,
} from "@phosphor-icons/react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";

const navItems = [
  { to: "/", icon: MagnifyingGlass, key: "nav.new", testid: "nav-new" },
  { to: "/analyses", icon: ListChecks, key: "nav.analyses", testid: "nav-analyses" },
  { to: "/settings", icon: Gear, key: "nav.settings", testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className="hidden md:flex flex-col w-64 shrink-0 bg-navy text-white sticky top-0 h-screen"
        data-testid="sidebar"
      >
        <div className="px-6 py-7 border-b border-white/10">
          <Logo variant="dark" />
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-white/10 text-white"
                      : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <Icon size={20} weight="regular" />
                {t(item.key)}
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <NavLink
              to="/admin/users"
              data-testid="nav-users"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActive ? "bg-white/10 text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <UsersThree size={20} weight="regular" />
              {t("nav.users")}
            </NavLink>
          )}
        </nav>

        <div className="px-3 pb-6 space-y-1">
          <button
            onClick={() => setLang(lang === "pt" ? "en" : "pt")}
            data-testid="lang-toggle"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-200"
          >
            <Translate size={20} />
            {lang === "pt" ? "Português" : "English"}
            <span className="ml-auto text-[11px] uppercase tracking-widest text-gold-bright">
              {lang === "pt" ? "EN" : "PT"}
            </span>
          </button>

          <div className="flex items-center gap-3 px-3 py-3 mt-2 rounded-md bg-white/5">
            <span className="flex items-center justify-center w-9 h-9 rounded-full bg-gold/20 text-gold-bright">
              <User size={18} weight="fill" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="user-name">
                {user?.name || t("nav.user")}
              </div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
          </div>

          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-slate-400 hover:text-white hover:bg-white/5 transition-colors duration-200"
          >
            <SignOut size={20} />
            {t("nav.logout")}
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-navy text-white px-4 py-3 flex items-center justify-between">
        <Logo variant="dark" size="sm" />
        <div className="flex items-center gap-1">
          <NavLink to="/" end className="p-2 rounded-md hover:bg-white/10" data-testid="m-nav-new">
            <MagnifyingGlass size={20} />
          </NavLink>
          <NavLink to="/analyses" className="p-2 rounded-md hover:bg-white/10" data-testid="m-nav-analyses">
            <ListChecks size={20} />
          </NavLink>
          <NavLink to="/settings" className="p-2 rounded-md hover:bg-white/10" data-testid="m-nav-settings">
            <Gear size={20} />
          </NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin/users" className="p-2 rounded-md hover:bg-white/10" data-testid="m-nav-users">
              <UsersThree size={20} />
            </NavLink>
          )}
          <button onClick={handleLogout} className="p-2 rounded-md hover:bg-white/10" data-testid="m-logout">
            <SignOut size={20} />
          </button>
        </div>
      </div>

      <main className="flex-1 min-w-0 pt-16 md:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
